import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Layout, Flex, Image, Empty, Row, Col, Button, Menu, Card } from "antd";
import { getApi, getParamsApi, loginApi } from "../services/api";
import axios from "axios";
import Checklist from "../components/checklist";
import Carousel from "../components/carousel";
import { DoubleRightOutlined, GiftOutlined } from "@ant-design/icons";
import Swal from "sweetalert2";

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const commonStyles = {
  width: "100%",
  margin: "0",
  display: "block",
};

const contentWrapperStyles = {
  ...commonStyles,
  padding: "15px",
};

const Main = () => {
  const query = useQuery();
  const [yinzcamId, setYinzcamId] = useState();
  const [email, setEmail] = useState();
  const { Content, Header } = Layout;
  const [loading, setLoading] = useState(false);
  const [contents, setContents] = useState([]);
  const [fanBadges, setFanBadges] = useState([]);
  const [userChecklist, setUserChecklist] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [checklistId, setChecklistId] = useState("");
  const [checklistItems, setChecklistItems] = useState([]);
  const [token, setToken] = useState();
  const [categoryDetails, setCategoryDetails] = useState({});
  const [categoryBadges, setCategoryBadges] = useState({});
  const [infoMessageList, setInfoMessageList] = useState({});
  const [rewards, setRewards] = useState([]);
  const [fanBadgesLoaded, setFanBadgesLoaded] = useState(false);

  useEffect(() => {
    // Send GA4 event for page view
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "page_view",
      page_title: "Fan Landing Page",
      page_location: window.location.href,
      user_id: yinzcamId || "anonymous",
      user_email: email || "anonymous",
      view_timestamp: new Date().toISOString(),
    });

    /*     ga4_measurement_id: "G-EC4X5M443P",

    if (window.gtag) {
      window.gtag("event", "page_view", {
        page_title: "Fan Landing Page",
        page_location: window.location.href,
        user_id: yinzcamId || "anonymous",
        user_email: email || "anonymous",
        view_timestamp: new Date().toISOString(),
      });
    } */
  }, [yinzcamId, email]);

  useEffect(() => {
    const yinzcamIdFromUrl = query.get("yinzcamId");
    const emailFromUrl = query.get("email");

    if (yinzcamIdFromUrl && emailFromUrl) {
      localStorage.setItem("yinzcamId", yinzcamIdFromUrl);
      localStorage.setItem("email", emailFromUrl);
      setYinzcamId(yinzcamIdFromUrl);
      setEmail(emailFromUrl);
    }
  }, [query]);

  useEffect(() => {
    if (yinzcamId && email && token) {
      getFanBadges(yinzcamId, email);
    }
  }, [yinzcamId, email, token]);

  useEffect(() => {
    if (fanBadgesLoaded) {
      getTemplate();
      getInfoMessage();
    }
  }, [fanBadgesLoaded]);

  const getToken = async () => {
    const params = new URLSearchParams();
    params.append("grant_type", "client_credentials");
    params.append("client_id", process.env.REACT_APP_CLIENT_ID);
    params.append("client_secret", process.env.REACT_APP_CLIENT_SECRET);
    const config = {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    };
    try {
      const response = await axios.post(
        process.env.REACT_APP_TOKEN_URL,
        params,
        config,
      );
      const newToken =
        response.data.token_type + " " + response.data.access_token;
      localStorage.setItem("token", newToken);
      setToken(newToken);
      const now = new Date();
      const expirationTime = new Date(
        now.getTime() + response.data.expires_in * 1000,
      );
      localStorage.setItem("tokenExpirationTime", expirationTime);
    } catch (error) {
      console.error("Error fetching the token:", error);
    }
  };

  useEffect(() => {
    const expiryTime = localStorage.getItem("tokenExpirationTime");
    const parsedExpiryTime = expiryTime ? new Date(expiryTime) : null;
    const storedToken = localStorage.getItem("token");

    if (
      storedToken &&
      parsedExpiryTime &&
      parsedExpiryTime.getTime() > new Date().getTime()
    ) {
      setToken(storedToken);
    } else {
      getToken();
    }
  }, []);

  useEffect(() => {
    if (categoryId) {
      getCategoryBadges(categoryId);
    }
  }, [categoryId]);

  useEffect(() => {
    if (checklistId) {
      getUserChecklist(checklistId);
    }
  }, [checklistId]);

  const waitForToken = () => {
    return new Promise((resolve) => {
      const checkToken = () => {
        if (token) {
          resolve();
        } else {
          setTimeout(checkToken, 100);
        }
      };
      checkToken();
    });
  };

  const getParamsApiWithTokenWait = async (url, params) => {
    await waitForToken(); // Ensure token is available before calling API
    return getParamsApi(url, params);
  };

  const getTemplate = () => {
    setLoading(true);
    getParamsApiWithTokenWait("/template/published", { deviceType: "MOBILE" })
      .then((template) => {
        if (template && template.isActive) {
          setContents(template.items);
          template.items.forEach((item) => {
            if (item.type === "CATEGORY") {
              setCategoryId(item.templateItemId);
              getCategoryBadges(item.templateItemId);
              getUserCategory(item.templateItemId);
            } else if (item.type === "CHECKLIST") {
              setChecklistId(item.templateItemId);
            }
          });
        } else {
          console.warn("No active template found");
          setContents([]);
        }
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  const getUserCategory = (id) => {
    setLoading(true);
    const params = { id: id };
    getParamsApiWithTokenWait(`/category/${id}`, params)
      .then((response) => {
        setCategoryDetails((prevDetails) => ({
          ...prevDetails,
          [id]: {
            name: response.name,
            placementType: response.placementType,
            infoMessage: response.infoMessage,
            iconURL: response.iconURL,
            backgroundImageURL: response.backgroundImageURL,
            fontColor: response.fontColor,
            font: response.font,
            lineColor: response.lineColor,
            shape: response.shape,
            layout: response.layout,
            paddingTop: response.paddingTop,
            paddingBottom: response.paddingBottom,
            paddingRight: response.paddingRight,
            paddingLeft: response.paddingLeft,
            infoBackgroundColor: response.infoBackgroundColor,
            infoFontColor: response.infoFontColor,
            tagBackgroundColor: response.tagBackgroundColor,
            tagFontColor: response.tagFontColor,
            tagMessage: response.tagMessage,
            buttonColor: response.buttonColor,
            buttonLinkURL: response.buttonLinkURL,
            buttonName: response.buttonName,
            buttonTextColor: response.buttonTextColor,
            emptyCategoryDescription: response.emptyCategoryDescription,
          },
        }));
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  const getCategoryBadges = (categoryId) => {
    setLoading(true);
    const params = { categoryId: categoryId };

    getParamsApiWithTokenWait(`/badge/category/${categoryId}`, params)
      .then((response) => {
        response.forEach((badge) => {
          console.log(
            `Badge: ${badge.name}, categoryOrder: ${badge.categoryOrder}, type: ${typeof badge.categoryOrder}`,
          );
        });

        const activeBadges = response
          .filter((badge) => badge.isActive)
          .sort((a, b) => {
            const orderA = parseInt(a.categoryOrder) || 999999;
            const orderB = parseInt(b.categoryOrder) || 999999;
            return orderA - orderB;
          });

        setCategoryBadges((prevBadges) => ({
          ...prevBadges,
          [categoryId]: activeBadges,
        }));
        setLoading(false);
      })
      .catch((error) => {
        console.error("getCategoryBadges Error:", error);
        setLoading(false);
      });
  };

  const getFanBadges = (yinzcamId, email) => {
    setLoading(true);
    const params = { yinzcamId, email };

    getParamsApiWithTokenWait(`/badge/fan`, params)
      .then((response) => {
        const activeBadges = (response.badgeFanDTOList || []).filter(
          (badge) => badge.isActive,
        );
        setFanBadges(activeBadges);

        const rewardsMap = new Map();

        activeBadges.forEach((badge) => {
          if (
            badge.rewardDescription ||
            badge.rewardDescriptionFontColor ||
            badge.rewardDescriptionFontSize ||
            badge.rewardHeadline ||
            badge.rewardHeadlineFontColor ||
            badge.rewardHeadlineFontSize ||
            badge.rewardImageURL
          ) {
            rewardsMap.set(badge.id, {
              badgeId: badge.id,
              rewardDetails: {
                rewardDescription: badge.rewardDescription || "",
                descriptionFontColor:
                  badge.rewardDescriptionFontColor || "#ffffff",
                descriptionFontSize: badge.rewardDescriptionFontSize || "12",
                headline: badge.rewardHeadline || "",
                headlineFontColor: badge.rewardHeadlineFontColor || "#ffffff",
                headlineFontSize: badge.rewardHeadlineFontSize || "12",
                imageURL: badge.rewardImageURL || "",
              },
            });
          }
        });

        const fanRewards = Array.from(rewardsMap.values());

        // Update rewards state with fan rewards
        setRewards((prevRewards) => {
          const newRewardsMap = new Map(
            prevRewards.map((reward) => [reward.badgeId, reward]),
          );

          fanRewards.forEach((reward) => {
            newRewardsMap.set(reward.badgeId, reward);
          });

          const updatedRewards = Array.from(newRewardsMap.values());
          return updatedRewards;
        });

        setLoading(false);
        setFanBadgesLoaded(true);
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: "Authentication Error",
          text: "We could not verify your credentials. Please check your login information or try again later.",
          confirmButtonColor: "#0050b5",
          confirmButtonText: "OK",
        });

        setLoading(false);
      });
  };

  const getUserChecklist = (checklistId) => {
    setLoading(true);
    const params = { id: checklistId };
    getParamsApiWithTokenWait(`/checklist/${checklistId}`, params)
      .then((response) => {
        setUserChecklist(response);
        setChecklistItems(response.items);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching checklist:", error);
        setLoading(false);
      });
  };

  const getInfoMessage = () => {
    setLoading(true);
    getParamsApiWithTokenWait("/infoMessage/list", {})
      .then((infoMessage) => {
        setInfoMessageList(infoMessage);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error while fetching info message:", error);
        setLoading(false);
      });
  };

  const navigate = useNavigate();

  const goRewardsPage = () => {
    navigate("/rewards", { state: { rewards } });
  };

  useEffect(() => {
    document.title = "Orlando Magic Fan";
  }, []);

  return (
    <Layout className="main-container" style={{ background: "#000" }}>
      <Header
        style={{
          background: "#A9A9A9",
          padding: 0,
          height: "40px",
          position: "sticky",
          top: 0,
          zIndex: 1,
          width: "100%",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        <Menu
          mode="horizontal"
          style={{
            lineHeight: "40px",
            borderBottom: "1px solid rgba(255, 255, 255, 0.03)",
            background: "#000",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Menu.Item
            key="gift"
            icon={<GiftOutlined style={{ color: "#fff" }} />}
            onClick={goRewardsPage}
            style={{
              marginRight: "20px",
              color: "#fff",
            }}
          >
            Rewards
          </Menu.Item>
        </Menu>
      </Header>

      <Content style={{ background: "#000", minHeight: "100vh" }}>
        <Row
          gutter={window.innerWidth > 768 ? [24, 24] : [0, 0]}
          style={{
            margin: 0,
            width: "100%",
            padding: 0,
            "@media (min-width: 769px)": {
              padding: "12px",
              paddingRight: "12px",
              paddingLeft: "12px",
              marginTop: "5px",
            },
          }}
        >
          {contents &&
            contents.length > 0 &&
            contents
              .sort((a, b) => a.order - b.order)
              .map((content) => {
                const categoryInfo = categoryDetails[content.templateItemId];
                const badgesForCategory =
                  categoryBadges[content.templateItemId] || [];
                const fanBadgesForCategory = fanBadges.filter(
                  (badge) => badge.categoryId === content.templateItemId,
                );
                const allBadges =
                  badgesForCategory.concat(fanBadgesForCategory);
                const info = Array.isArray(infoMessageList)
                  ? infoMessageList.find(
                      (infoMessage) =>
                        infoMessage.id === content.templateItemId,
                    ) || null
                  : null;

                const contentStyles = content.styles
                  ? {
                      width: "100%",
                      maxWidth: "100%",
                      ...JSON.parse(content.styles),
                    }
                  : {
                      width: "100%",
                      maxWidth: "100%",
                    };

                const combinedBadges = allBadges
                  .reduce((acc, badge) => {
                    const existingBadge = acc.find(
                      (item) => item.id === badge.id,
                    );
                    const fanVersion = fanBadgesForCategory.find(
                      (fanBadge) => fanBadge.id === badge.id,
                    );

                    if (existingBadge) {
                      existingBadge.isEarned = fanVersion
                        ? fanVersion.badgeCompletion
                        : existingBadge.isEarned || false;
                    } else {
                      acc.push({
                        ...badge,
                        isEarned: fanVersion
                          ? fanVersion.badgeCompletion
                          : false,
                      });
                    }
                    return acc;
                  }, [])
                  .sort((a, b) => {
                    const orderA = parseInt(a.categoryOrder) || 999999;
                    const orderB = parseInt(b.categoryOrder) || 999999;
                    return orderA - orderB;
                  });

                const displayedBadges = combinedBadges.filter((badge) => {
                  if (badge.isEarned) {
                    return true;
                  }
                  return badge.isDisplayUnearned === true;
                });

                const contentElement = (() => {
                  switch (content.type) {
                    case "TEXT":
                      return (
                        <div style={{ ...contentStyles, padding: "10px 0px" }}>
                          <p
                            dangerouslySetInnerHTML={{
                              __html: content.title || "Default Text",
                            }}
                            style={{
                              margin: 0,
                              textAlign: "left",
                              ...contentStyles,
                            }}
                          />
                        </div>
                      );
                    case "INFO":
                      return (
                        info && (
                          <div
                            key={info.id}
                            style={{
                              ...contentWrapperStyles,
                              background: info.backgroundColor,
                              backgroundImage: `url(${info.backgroundImageURL})`,
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                              borderRadius: "5px",
                              ...contentStyles,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                              }}
                            >
                              {info.iconURL && (
                                <img
                                  src={info.iconURL}
                                  alt="Icon"
                                  style={{ width: "35px", height: "35px" }}
                                />
                              )}
                              <span style={{ color: "#fff", flex: 1 }}>
                                {info.description}
                              </span>
                            </div>
                          </div>
                        )
                      );
                    case "LINK":
                      return (
                        <div style={{ ...commonStyles, ...contentStyles }}>
                          <a
                            className="custom-link"
                            href={content.description}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ display: "block" }}
                          >
                            {content.title || "Default Text"}
                          </a>
                        </div>
                      );
                    case "IMAGE":
                      return (
                        <Col
                          span={24}
                          key={content.templateItemId}
                          style={{ marginBottom: "12px" }}
                        >
                          <div
                            style={{
                              ...commonStyles,
                              overflow: "hidden",
                              ...contentStyles,
                            }}
                          >
                            <img
                              src={content.url}
                              alt={content.description || "Image"}
                              style={{
                                objectFit: "contain",
                                display: "block",
                                ...contentStyles,
                              }}
                            />
                          </div>
                        </Col>
                      );
                    case "VIDEO":
                      return (
                        <div
                          key={content.templateItemId}
                          className="video-responsive"
                          style={{
                            ...commonStyles,
                            position: "relative",
                            paddingBottom: "56.25%",
                            height: 0,
                            ...contentStyles,
                          }}
                        >
                          <iframe
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                            }}
                            src={content.url}
                            allow="autoplay; encrypted-media"
                            allowFullScreen
                            title={content.title || "Video"}
                          />
                        </div>
                      );
                    case "CHECKLIST":
                      return (
                        <div style={{ ...commonStyles, ...contentStyles }}>
                          <Checklist
                            data={userChecklist}
                            items={checklistItems}
                            fanBadges={fanBadges}
                          />
                        </div>
                      );
                    case "CATEGORY":
                      return (
                        <div
                          key={content.templateItemId}
                          style={{
                            ...commonStyles,
                            borderRadius:
                              categoryInfo?.shape === "Rounded" ? "10px" : "0",
                            padding: `${categoryInfo?.paddingTop || "15spx"} ${
                              categoryInfo?.paddingRight || "15px"
                            } ${categoryInfo?.paddingBottom || "15px"} ${
                              categoryInfo?.paddingLeft || "15px"
                            }`,
                            backgroundImage: `url(${
                              categoryInfo?.backgroundImageURL || ""
                            })`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            backgroundRepeat: "no-repeat",
                            overflow: "hidden",
                            ...contentStyles,
                          }}
                        >
                          <Flex
                            align="center"
                            style={{
                              margin: "0 15px",
                              borderBottom: `1px solid ${
                                categoryInfo?.lineColor || "white"
                              }`,
                            }}
                          >
                            {categoryInfo?.iconURL && (
                              <Image
                                src={categoryInfo.iconURL}
                                alt="Category Icon"
                                className="category-icon"
                                style={{ width: "25px" }}
                              />
                            )}
                            <h1
                              className="landing-headline"
                              style={{
                                color: categoryInfo?.fontColor || "#fff",
                              }}
                            >
                              {categoryInfo?.name || ""}
                            </h1>
                          </Flex>

                          {(categoryInfo?.infoMessage ||
                            categoryInfo?.tagMessage) && (
                            <Flex
                              className="info-card"
                              style={{
                                background: categoryInfo.infoBackgroundColor,
                                color: categoryInfo.infoFontColor,
                              }}
                            >
                              {categoryInfo?.tagMessage && (
                                <div
                                  className="info-tag"
                                  style={{
                                    background:
                                      categoryInfo?.tagBackgroundColor,
                                    color: categoryInfo?.tagFontColor,
                                  }}
                                >
                                  {categoryInfo.tagMessage}
                                </div>
                              )}
                              {categoryInfo?.infoMessage && (
                                <div
                                  className="info-message"
                                  style={{
                                    color: categoryInfo.infoFontColor,
                                    background:
                                      categoryInfo.infoBackgroundColor,
                                  }}
                                >
                                  {categoryInfo.infoMessage}
                                </div>
                              )}
                            </Flex>
                          )}
                          {combinedBadges.length > 0 ? (
                            <div
                              style={{
                                margin: `0 ${
                                  categoryInfo?.paddingLeft || "15px"
                                } 0 ${categoryInfo?.paddingRight || "15px"}`,
                              }}
                            >
                              {displayedBadges.length > 0 ? (
                                <Carousel slides={displayedBadges} />
                              ) : (
                                <Card
                                  style={{
                                    backgroundColor: "black",
                                    textAlign: "center",
                                    border: "none",
                                  }}
                                >
                                  <div
                                    style={{ color: "white", opacity: ".5" }}
                                    dangerouslySetInnerHTML={{
                                      __html:
                                        categoryInfo?.emptyCategoryDescription ||
                                        "",
                                    }}
                                  />
                                </Card>
                              )}
                            </div>
                          ) : (
                            <Card
                              style={{
                                backgroundColor: "black",
                                textAlign: "center",
                                border: "none",
                              }}
                            >
                              <div
                                style={{ color: "white", opacity: ".5" }}
                                dangerouslySetInnerHTML={{
                                  __html:
                                    categoryInfo?.emptyCategoryDescription ||
                                    "",
                                }}
                              />
                            </Card>
                          )}
                          {categoryInfo?.buttonLinkURL && (
                            <a
                              style={{
                                position: "relative",
                                left: "50%",
                                transform: "translateX(-50%)",
                                display: "inline-block",
                                color: categoryInfo.buttonTextColor,
                                backgroundColor: categoryInfo.buttonColor,
                                textDecoration: "none",
                                borderRadius: "5px",
                                padding: "5px 5px 5px 10px",
                                width: "120px",
                                margin: "10px 0",
                              }}
                              href={`https://${categoryInfo.buttonLinkURL}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Flex align="center">
                                <span
                                  style={{
                                    marginRight: "5px",
                                    fontSize: "13px",
                                  }}
                                >
                                  {categoryInfo.buttonName}
                                </span>
                                <DoubleRightOutlined
                                  style={{ opacity: ".5" }}
                                />
                              </Flex>
                            </a>
                          )}
                        </div>
                      );
                    default:
                      return null;
                  }
                })();

                return content.type === "IMAGE" ? (
                  contentElement
                ) : (
                  <Col
                    xs={24}
                    sm={24}
                    md={12}
                    lg={12}
                    key={content.templateItemId}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      padding: 0,
                      marginTop: "10px",
                      "@media (min-width: 769px)": {
                        padding: "0 12px",
                        paddingRight: "12px",
                        paddingLeft: "12px",
                        marginTop: "5px",
                      },
                    }}
                  >
                    {contentElement}
                  </Col>
                );
              })}
        </Row>
      </Content>
    </Layout>
  );
};

export default Main;
